import { inject, Injectable } from '@angular/core';
import {
    ConfigConstants,
    NOTIFICATION_STATUS_CODE,
    NotificationDetailState,
    NotificationStateUtilities,
    NotificationUpdateService,
    selectDisablePaceForms,
    selectEnableRemoteWork,
    selectEnableSafetyAssessmentForm,
    selectShowFCCForNotificationTypes,
    selectValueHelpersById,
    stateToNotificationIdModel,
    STATUS_STEP_FOR_NON_REMOTE_WORK,
    STATUS_STEP_FOR_REMOTE_WORK,
    statusStateToModel,
    TIMERECORDING_TYPE_OF_WORK_CODES,
} from '@evorait/pace-core';
import { PaceStatusStepButton, PaceStatusStepEntry } from '@evorait/pace-components';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { STATUS_STEP_ACTION } from '../status-step-button/status-step-action.enum';

/** The statuses the step button offers PACE Forms in, on top of to-be-signed and remote work. */
const FORM_STATUSES = new Set<NOTIFICATION_STATUS_CODE>([
    NOTIFICATION_STATUS_CODE.ETA,
    NOTIFICATION_STATUS_CODE.DURA,
    NOTIFICATION_STATUS_CODE.TRAV,
    NOTIFICATION_STATUS_CODE.OSR,
    NOTIFICATION_STATUS_CODE.SOSR,
    NOTIFICATION_STATUS_CODE.ATS,
]);

/** The statuses that put a remote status at the bottom of the list, so the last two get dimmed. */
const REMOTE_FIRST_STATUSES = new Set<NOTIFICATION_STATUS_CODE>([
    NOTIFICATION_STATUS_CODE.DISP,
    NOTIFICATION_STATUS_CODE.ETA,
    NOTIFICATION_STATUS_CODE.DURA,
    NOTIFICATION_STATUS_CODE.REMT,
]);

/** The dropdown entries that carry the remote-work phone icon. */
const REMOTE_ICON_ACTIONS = new Set<string>([STATUS_STEP_ACTION.ROSR, STATUS_STEP_ACTION.FCC]);

/**
 * Builds the {@link PaceStatusStepButton} view model from the notification store's
 * {@link NotificationDetailState}.
 *
 * The available statuses come from `selectAvailableStatusForId`, which already applies the
 * transition rules; this only adds the parts the store leaves out — the translated labels, the icons
 * and the ordering, all of them config- and value-help-dependent.
 */
@Injectable({ providedIn: 'root' })
export class NotificationDetailsStepButtonFactory {
    private readonly log: LogInterface = inject(LogInstanceToken);
    private readonly translate = inject(TranslateService);
    private readonly notificationUpdateService = inject(NotificationUpdateService);
    private readonly store = inject(Store);

    private readonly enableSafetyAssessmentForm = this.store.selectSignal(selectEnableSafetyAssessmentForm);
    private readonly enableRemoteWork = this.store.selectSignal(selectEnableRemoteWork);
    private readonly disablePaceForms = this.store.selectSignal(selectDisablePaceForms);
    private readonly timeRecordingTaskTypeInfo = this.store.selectSignal(
        selectValueHelpersById(ConfigConstants.valueHelperIDTimeRecordingTaskTypeInfo),
    );

    /**
     * @param state the notification's detail state
     * @param isDropup whether the button renders in the smartphone bottom bar
     * @param isBusy whether a status change is currently in flight
     * @param selectedActionId the action to show on the main half, or `undefined` for the first one
     */
    public create(
        state: NotificationDetailState,
        isDropup: boolean,
        isBusy: boolean,
        selectedActionId?: string,
    ): PaceStatusStepButton {
        const actions = this.getActions(state);
        const primary = actions.find((action) => action.id === selectedActionId) ?? actions[0];

        this.log.debug(
            `NotificationDetailsStepButtonFactory: notification ${NotificationStateUtilities.notificationIdToString(state.notificationId)} has local status '${state.status.statusCode}'; step entries: ${actions.map((action) => action.label).join(', ')}`,
        );

        return {
            primary,
            primaryOpensDropdown: primary?.id === STATUS_STEP_ACTION.REMT,
            // The main half already offers the primary action, so the dropdown lists the rest.
            entries: actions
                .filter((action) => action.id !== primary?.id)
                .map((action) => ({ ...action, isDisabled: this.isDisabled(action, primary) })),
            dimClass: this.getDimClass(state),
            isDropup,
            isBusy,
            menuAriaLabel: <string>this.translate.instant('NOTIFICATION_STATUS_STEP_BUTTON_MENU_ARIA_LABEL'),
        };
    }

    /**
     * The actions the notification's current status allows, in the order they are offered.
     *
     * @param state the notification's detail state
     * @returns the actions, the first of which lands on the main half of the button
     */
    private getActions(state: NotificationDetailState): PaceStatusStepEntry[] {
        const available = new Set<NOTIFICATION_STATUS_CODE>(state.availableStatus);
        const status = statusStateToModel(state.status);
        const actions: PaceStatusStepEntry[] = [];

        const add = (action: STATUS_STEP_ACTION, label: string): void => {
            actions.push({ id: action, label, showRemoteIcon: REMOTE_ICON_ACTIONS.has(action), isDisabled: false });
        };

        if (available.has(NOTIFICATION_STATUS_CODE.ETA)) {
            add(STATUS_STEP_ACTION.ETA, <string>this.translate.instant('NOTIFICATION_STATUS_ETA_LABEL'));
        }
        if (available.has(NOTIFICATION_STATUS_CODE.TRAV)) {
            add(STATUS_STEP_ACTION.TRAV, <string>this.translate.instant('NOTIFICATION_STATUS_TRAV_LABEL'));
        }
        if (available.has(NOTIFICATION_STATUS_CODE.OSR)) {
            add(STATUS_STEP_ACTION.OSR, <string>this.translate.instant('NOTIFICATION_STATUS_OSR_LABEL'));
        }
        if (this.enableSafetyAssessmentForm() && available.has(NOTIFICATION_STATUS_CODE.ATS)) {
            add(STATUS_STEP_ACTION.ATS, <string>this.translate.instant('NOTIFICATION_STATUS_SAF_LABEL'));
        }
        // SOSR: Special OSR for PACE US
        if (available.has(NOTIFICATION_STATUS_CODE.SOSR)) {
            add(STATUS_STEP_ACTION.SOSR, <string>this.translate.instant('NOTIFICATION_STATUS_SOSR_LABEL'));
        }
        if (available.has(NOTIFICATION_STATUS_CODE.CRM)) {
            add(STATUS_STEP_ACTION.CRM, <string>this.translate.instant('NOTIFICATION_STATUS_CRM_LABEL'));
        }
        if (available.has(NOTIFICATION_STATUS_CODE.SCRM) && this.isShortConfirmationOffered(state)) {
            // For PACE US a short confirmation, when available, belongs on top.
            actions.unshift({
                id: STATUS_STEP_ACTION.SCRM,
                label: <string>this.translate.instant('NOTIFICATION_STATUS_SCRM_LABEL'),
                showRemoteIcon: false,
                isDisabled: false,
            });
        }
        if (status.canEscalate()) {
            add(STATUS_STEP_ACTION.ESCALATE, <string>this.translate.instant('NOTIFICATION_STATUS_HSCE_LABEL'));
        }
        if (
            this.isPaceFormsEnabled(state) &&
            (FORM_STATUSES.has(state.status.statusCode) || status.isToBeSigned() || status.isRemoteWork())
        ) {
            add(STATUS_STEP_ACTION.FORMS, <string>this.translate.instant('NOTIFICATION_STATUS_F4M_LABEL'));
        }

        if (!this.enableRemoteWork()) {
            return actions;
        }
        return this.withRemoteWorkActions(state, actions);
    }

    /**
     * Adds the remote statuses and re-orders the list the way remote work requires: a notification
     * planned as remote support leads with the remote statuses, any other one with the on-site ones.
     *
     * @param state the notification's detail state
     * @param actions the actions collected so far
     * @returns the actions in their final order
     */
    private withRemoteWorkActions(
        state: NotificationDetailState,
        actions: PaceStatusStepEntry[],
    ): PaceStatusStepEntry[] {
        const available = new Set<NOTIFICATION_STATUS_CODE>(state.availableStatus);
        const isRemoteSupport = state.userStatus.includes(ConfigConstants.USER_STATUS.REMT);

        if (available.has(NOTIFICATION_STATUS_CODE.REMT) && isRemoteSupport) {
            actions.push({
                id: STATUS_STEP_ACTION.REMT,
                label: <string>this.translate.instant('NOTIFICATION_STATUS_REMT_LABEL'),
                showRemoteIcon: false,
                isDisabled: false,
            });
        }

        const timeRecordingTasks = this.timeRecordingTaskTypeInfo();
        const remoteWorkLabel = timeRecordingTasks.some((task) => task.code === TIMERECORDING_TYPE_OF_WORK_CODES.ROSN)
            ? <string>this.translate.instant('NOTIFICATION_STATUS_ROSR_LABEL')
            : timeRecordingTasks.find((task) => task.code === NOTIFICATION_STATUS_CODE.ROSR)?.descText;
        if (available.has(NOTIFICATION_STATUS_CODE.ROSR) && remoteWorkLabel) {
            actions.push({
                id: STATUS_STEP_ACTION.ROSR,
                label: remoteWorkLabel,
                showRemoteIcon: true,
                isDisabled: false,
            });
        }

        // Remote clarification (FCC) is only allowed for the notification types the gateway lists.
        if (
            available.has(NOTIFICATION_STATUS_CODE.FCC) &&
            this.store.selectSignal(selectShowFCCForNotificationTypes(state.notificationType))()
        ) {
            const remoteClarificationLabel = timeRecordingTasks.some(
                (task) => task.code === TIMERECORDING_TYPE_OF_WORK_CODES.FCCN,
            )
                ? <string>this.translate.instant('NOTIFICATION_STATUS_FCC_LABEL')
                : timeRecordingTasks.find((task) => task.code === NOTIFICATION_STATUS_CODE.FCC)?.descText;
            if (remoteClarificationLabel) {
                actions.push({
                    id: STATUS_STEP_ACTION.FCC,
                    label: remoteClarificationLabel,
                    showRemoteIcon: true,
                    isDisabled: false,
                });
            }
        }

        const statusOrder = Object.values(
            statusStateToModel(state.status).isOnSiteWork() || !isRemoteSupport
                ? STATUS_STEP_FOR_NON_REMOTE_WORK
                : STATUS_STEP_FOR_REMOTE_WORK,
        );
        return [...actions].sort((a, b) => statusOrder.indexOf(a.id) - statusOrder.indexOf(b.id));
    }

    /**
     * A short confirmation is only offered while the notification is neither cancelled nor waiting
     * for a cancellation to be sent, and while its last day has not started yet.
     *
     * @param state the notification's detail state
     */
    private isShortConfirmationOffered(state: NotificationDetailState): boolean {
        const isCancelled =
            statusStateToModel(state.status).isCancelled() ||
            state.isCancelled.length > 0 ||
            this.notificationUpdateService.checkIfCancellationExists(stateToNotificationIdModel(state.notificationId));
        return !isCancelled && moment().startOf('day').isBefore(state.end.clone().startOf('day'));
    }

    private isPaceFormsEnabled(state: NotificationDetailState): boolean {
        return !this.disablePaceForms() && state.formsCount > 0;
    }

    /**
     * The Safety Assessment form has to be filled in before the on-site status can be set, so OSR
     * stays disabled while the form is the primary action.
     *
     * Keyed on the action id rather than on the rendered label, so the check does not depend on the
     * app's language.
     *
     * @param action the dropdown entry
     * @param primary the action on the main half of the button
     */
    private isDisabled(action: PaceStatusStepEntry, primary: PaceStatusStepEntry | undefined): boolean {
        return (
            this.enableSafetyAssessmentForm() &&
            action.id === STATUS_STEP_ACTION.OSR &&
            primary?.id === STATUS_STEP_ACTION.ATS
        );
    }

    /**
     * Which of the low-priority entries at the bottom of the dropdown are dimmed: the last two when
     * a remote and an on-site status sit there together, the last one when a notification that is
     * neither remote nor FCC-enabled ends on ROSR, and none otherwise.
     *
     * @param state the notification's detail state
     */
    private getDimClass(state: NotificationDetailState): ConfigConstants.DimStatus {
        const status = statusStateToModel(state.status);
        const isRemoteSupport = this.enableRemoteWork() && state.userStatus.includes(ConfigConstants.USER_STATUS.REMT);
        const showFCCStatus = this.store.selectSignal(selectShowFCCForNotificationTypes(state.notificationType))();

        if (!status.isInProgress() && !isRemoteSupport && !showFCCStatus) {
            return ConfigConstants.DimStatus.LastStatus;
        }
        if (this.enableRemoteWork() && REMOTE_FIRST_STATUSES.has(state.status.statusCode)) {
            return ConfigConstants.DimStatus.LastTwoStatus;
        }
        return ConfigConstants.DimStatus.Default;
    }
}
